// @flow
/** @jsx jsx */

import * as React from 'react';
import MarkdownParser from 'markdown-to-jsx';

import Link from '@zapier/common-components/src/components/Link';
import Text from '@zapier/common-components/src/components/Text';
import { Colors } from '@zapier/theme';
import { Image } from '@zapier/common-components/src/components/Image';
import { jsx } from '@emotion/core';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@zapier/common-components/src/components/SimpleTable';

type Props = {
  /** markdown source string */
  source: string,
  /** CSS to be applied to code tags inside pre */
  codeBlockCss?: any,
  /** Css to be applied to span tags */
  spanCss?: any,
  /** CSS to be applied to code tags lines */
  codeLineCss?: any,
};

const headingLevelToType = [
  '',
  'mega-head',
  'sub-head',
  'section-head',
  'body-large-strong',
  'subsection-head',
  'body',
  'body',
];

const Blockquote = ({ children }: { children: React.Node }) => {
  return (
    <blockquote
      css={{
        borderLeft: `0.25em solid ${Colors.adamantium}`,
        color: Colors.steel,
        margin: 0,
        padding: '0 1em',
      }}
    >
      {children}
    </blockquote>
  );
};

const CodeBlock = ({ children, css }: { children: React.Node, css: any }) => {
  return (
    <pre>
      {Array.isArray(children) ? (
        children
      ) : (
        <code css={{ whiteSpace: 'pre', ...css }}>
          {React.Children.only(children).props.children}
        </code>
      )}
    </pre>
  );
};

const CodeLine = (props: { children: React.Node, css: any }) => {
  return <code css={props.css}>{props.children}</code>;
};

const Heading = (props: { children: React.Node, level: number }) => {
  const Tag = `h${props.level}`;
  return (
    <Tag>
      <Text type={headingLevelToType[props.level]} {...props} />
    </Tag>
  );
};

const Span = (props: { children: React.Node, css: any }) => {
  return <span css={props.css}>{props.children}</span>;
};

/**
 * Renders markdown to React components using `react-markdown` and our own components
 */
const Markdown = ({ source, codeBlockCss, spanCss, codeLineCss }: Props) => {
  const options = {
    overrides: {
      a: { component: Link },
      blockquote: { component: Blockquote },
      h1: { component: Heading, props: { level: 1 } },
      h2: { component: Heading, props: { level: 2 } },
      h3: { component: Heading, props: { level: 3 } },
      h4: { component: Heading, props: { level: 4 } },
      h5: { component: Heading, props: { level: 5 } },
      h6: { component: Heading, props: { level: 6 } },
      img: { component: Image },
      pre: { component: CodeBlock, props: { css: codeBlockCss } },
      code: { component: CodeLine, props: { css: codeLineCss } },
      table: { component: Table },
      tbody: { component: TableBody },
      td: { component: TableCell },
      th: { component: TableHeadCell },
      thead: { component: TableHead },
      tr: { component: TableRow },
      span: { component: Span, props: { css: spanCss } },
    },
  };
  return <MarkdownParser children={source} options={options} />;
};

export default Markdown;
